import { useState, lazy, Suspense } from 'react';
import { Flex, useDisclosure, MenuItem } from '@chakra-ui/react';
import { Copy03, PlusSquare } from '@kula-ai/icons-core';
import { KMenuButton } from '@/ui-library/components';
import { KText } from '@/ui-library/components/text';
import useUrlState from '@ahooksjs/use-url-state';
// Only mounted once the create menu is used, so they should not be in the
// route's critical chunk — they drag @/components/formBuilder in with them.
const CreateRequisitions = lazy(() =>
  import('@/components/shared/requisitions/components/CreateRequisitions').then(
    (m) => ({ default: m.CreateRequisitions })
  )
);
const RequisitionCloneModal = lazy(() =>
  import('@/components/shared/requisitions/components/RequisitionCloneModal').then(
    (m) => ({ default: m.RequisitionCloneModal })
  )
);
import { API_SERVICE } from '@/components/shared/requisitions/api';
import { useShowToast } from '@/hooks';
import { useAtsPermissions } from '@/hooks/useAtsPermissions';
import { getRequisitionErrorMessage } from '@/components/shared/requisitions/utils/requisitionErrorHandler';

export const useRequisitionsHeaderActions = () => {
  const createActions = useDisclosure();
  const [selectedAction, setSelectedAction] = useState(undefined);
  const [, setUrlState] = useUrlState();
  const toast = useShowToast();
  const { requisitionsPermissions } = useAtsPermissions();

  const requisitionActionsConfig = [
    {
      id: 'createRequisition',
      icon: PlusSquare,
      label: 'Start from scratch',
      description: 'Create a requisition with a blank canvas',
      modalActionText: 'Create',
      onClick: createActions.onOpen,
    },
    {
      id: 'cloneRequisition',
      icon: Copy03,
      label: 'Clone requisition',
      description: 'Create a copy of an existing requisition as draft',
      modalActionText: 'Clone',
      onClick: createActions.onOpen,
      apiCall: async (payload: any, onClose: () => void) => {
        try {
          const response = await API_SERVICE.cloneRequisition(
            payload.cloneRequisitionId
          );
          toast({ title: 'Requisition cloned successfully' });
          onClose();
          // Open the cloned draft for editing
          setUrlState({ draft: response.requisition.id.toString() });
        } catch (error) {
          const errorMessage = getRequisitionErrorMessage(error, false);
          toast({
            title: 'Failed to clone requisition',
            description: errorMessage || 'Please try again',
            status: 'error',
          });
        }
      },
    },
  ];

  const menuContent = () => {
    return requisitionActionsConfig.map((item, index) => {
      const { label, description, onClick } = item;

      const isFirst = index === 0;
      const isLast = index === requisitionActionsConfig.length - 1;

      return (
        <MenuItem
          key={index}
          onClick={() => {
            setSelectedAction(item);
            onClick();
          }}
          p="0"
          borderBottom={!isLast && '1px solid'}
          borderColor={!isLast && 'border-color'}
          _hover={{ bg: 'secondary-50' }}
          borderTopRadius={isFirst ? '8px' : '0'}
          borderBottomRadius={isLast ? '8px' : '0'}
        >
          <Flex gap="1rem" p="13px 20px" alignItems="center">
            <Flex flexDir="column" maxW="250px">
              <KText size="sm-medium" color="secondary-900">
                {label}
              </KText>
              <KText size="xs-regular" color="secondary-700">
                {description}
              </KText>
            </Flex>
          </Flex>
        </MenuItem>
      );
    });
  };

  const renderCreateAction = () => {
    return (
      <KMenuButton
        variant="primary"
        triggerContent="Create requisition"
        menuContent={menuContent()}
        menuContentProps={{ p: 0, borderRadius: '8px' }}
      />
    );
  };

  const headerActions = () => {
    if (!requisitionsPermissions.create) return null;

    return (
      <Flex gap="6px" alignItems="center" w="max-content">
        {renderCreateAction()}
      </Flex>
    );
  };

  const modal = createActions.isOpen ? (
    <Suspense fallback={null}>
      {selectedAction?.id === 'cloneRequisition' ? (
        <RequisitionCloneModal
          isOpen={createActions.isOpen}
          onClose={createActions.onClose}
          config={selectedAction}
        />
      ) : (
        <CreateRequisitions
          isOpen={createActions.isOpen}
          onClose={createActions.onClose}
        />
      )}
    </Suspense>
  ) : null;

  return {
    headerActions,
    modal,
  };
};
